import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    ArrowLeft, Users, Upload, ShieldCheck, RefreshCcw, Send,
    CheckCircle2, XCircle, AlertTriangle,
} from "lucide-react";

/**
 * /admin/marketing — VAULT Marketing Centre (Contacts + Campaigns + Readiness).
 * Consent-safe: never displays financial data. Every write path goes through
 * the backend which enforces consent-source-when-consent-true.
 */
const STATUS_STYLE = {
    PASS:         { bg: "bg-emerald-50", fg: "text-emerald-700",  border: "border-emerald-200", Icon: CheckCircle2 },
    PASS_DRY_RUN: { bg: "bg-emerald-50", fg: "text-emerald-700",  border: "border-emerald-200", Icon: CheckCircle2 },
    DRAFT_READY:  { bg: "bg-emerald-50", fg: "text-emerald-700",  border: "border-emerald-200", Icon: CheckCircle2 },
    BLOCKED:      { bg: "bg-amber-50",   fg: "text-amber-700",    border: "border-amber-200",   Icon: AlertTriangle },
    FAIL:         { bg: "bg-rose-50",    fg: "text-rose-700",     border: "border-rose-200",    Icon: XCircle },
};

function StatusPill({ status }) {
    const s = STATUS_STYLE[status] || STATUS_STYLE.BLOCKED;
    return (
        <span className={`inline-flex items-center gap-1 rounded-full font-semibold px-2 py-0.5 text-[11px] ${s.bg} ${s.fg} border ${s.border}`}>
            <s.Icon size={12} /> {status}
        </span>
    );
}

function TabButton({ active, label, onClick, testid }) {
    return (
        <button data-testid={testid} onClick={onClick}
            className={`px-3 py-1.5 text-sm rounded-full transition ${
                active ? "bg-[#0b1e3f] text-[#f6f3ec]"
                       : "text-stone-700 hover:bg-stone-100 border border-stone-200"}`}>
            {label}
        </button>
    );
}

export default function MarketingCentre() {
    const [tab, setTab]                 = useState("dashboard");
    const [readiness, setReadiness]     = useState(null);
    const [analytics, setAnalytics]     = useState(null);
    const [contacts, setContacts]       = useState([]);
    const [contactQ, setContactQ]       = useState("");
    const [campaigns, setCampaigns]     = useState([]);
    const [importText, setImportText]   = useState("");
    const [importPreview, setImportPreview] = useState(null);
    const [contactForm, setContactForm] = useState({
        email: "", first_name: "", last_name: "", source: "WEBSITE",
        marketing_consent: false, consent_source: "",
    });
    const [showContactDialog, setShowContactDialog] = useState(false);
    const [busy, setBusy] = useState(false);

    const loadReadiness = async () => {
        try {
            const r = await api.get("/api/marketing/readiness");
            setReadiness(r.data);
        } catch (e) { toast.error("Failed to load readiness report"); }
    };
    const loadAnalytics = async () => {
        try {
            const r = await api.get("/api/marketing/analytics/summary");
            setAnalytics(r.data);
        } catch (_e) { /* ignore */ }
    };
    const loadContacts = async () => {
        try {
            const r = await api.get("/api/marketing/contacts", {
                params: { q: contactQ || undefined, limit: 50 },
            });
            setContacts(r.data.rows || []);
        } catch (_e) { toast.error("Failed to load contacts"); }
    };
    const loadCampaigns = async () => {
        try {
            const r = await api.get("/api/marketing/campaigns");
            setCampaigns(r.data.campaigns || []);
        } catch (_e) { toast.error("Failed to load campaigns"); }
    };

    useEffect(() => {
        loadReadiness(); loadAnalytics(); loadContacts(); loadCampaigns();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const consentBlockValid = useMemo(() =>
        !contactForm.marketing_consent
            || (contactForm.consent_source || "").trim().length > 0,
    [contactForm]);

    const saveContact = async () => {
        if (!consentBlockValid) {
            toast.error("Consent source is required when marketing consent is on");
            return;
        }
        setBusy(true);
        try {
            await api.post("/api/marketing/contacts", contactForm);
            toast.success("Contact saved");
            setShowContactDialog(false);
            setContactForm({ email: "", first_name: "", last_name: "",
                source: "WEBSITE", marketing_consent: false, consent_source: "" });
            loadContacts(); loadAnalytics(); loadReadiness();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    const previewImport = async () => {
        setBusy(true);
        try {
            const r = await api.post("/api/marketing/contacts/import/preview",
                { csv_text: importText });
            setImportPreview(r.data);
        } catch (e) {
            toast.error("Preview failed");
        } finally { setBusy(false); }
    };

    const commitImport = async () => {
        setBusy(true);
        try {
            const r = await api.post("/api/marketing/contacts/import/commit",
                { csv_text: importText });
            toast.success(`Imported ${r.data.inserted} · updated ${r.data.updated} · skipped ${r.data.skipped}`);
            setImportText(""); setImportPreview(null);
            loadContacts(); loadAnalytics(); loadReadiness();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import failed");
        } finally { setBusy(false); }
    };

    const seedFirstCampaign = async () => {
        try {
            await api.post("/api/marketing/seed/first-campaign");
            toast.success("VAULT_LAUNCH_01 draft ready");
            loadCampaigns(); loadReadiness();
        } catch (_e) { toast.error("Seed failed"); }
    };

    return (
        <div data-testid="marketing-centre" className="min-h-screen bg-[#f6f3ec] text-[#0b1e3f]">
            <div className="max-w-6xl mx-auto p-6">
                <div className="flex items-center gap-3 mb-6">
                    <Link to="/dashboard" className="text-stone-500 hover:text-stone-800">
                        <ArrowLeft size={18} />
                    </Link>
                    <h1 className="font-['Fraunces',serif] text-2xl">Marketing Centre</h1>
                    <span className="ml-auto text-xs text-stone-500">VAULT owns the canonical marketing database · Brevo = delivery engine only</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                    <TabButton active={tab==="dashboard"} label="Dashboard"  testid="mkt-tab-dashboard"  onClick={()=>setTab("dashboard")} />
                    <TabButton active={tab==="contacts"}  label="Contacts"   testid="mkt-tab-contacts"   onClick={()=>setTab("contacts")} />
                    <TabButton active={tab==="import"}    label="Import CSV" testid="mkt-tab-import"     onClick={()=>setTab("import")} />
                    <TabButton active={tab==="campaigns"} label="Campaigns"  testid="mkt-tab-campaigns"  onClick={()=>setTab("campaigns")} />
                    <TabButton active={tab==="readiness"} label="Readiness"  testid="mkt-tab-readiness"  onClick={()=>setTab("readiness")} />
                </div>

                {tab === "dashboard" && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="mkt-dashboard">
                        {[
                            ["Audience total", analytics?.audience_total ?? "—"],
                            ["Consented audience", analytics?.audience_consented ?? "—"],
                            ["New (7d)", analytics?.new_subscribers_7d ?? "—"],
                            ["Unsubscribes (7d)", analytics?.unsubscribes_7d ?? "—"],
                            ["Campaigns sent", analytics?.campaigns_sent ?? "—"],
                            ["Suppression list", analytics?.suppressed ?? "—"],
                        ].map(([label, val]) => (
                            <Card key={label}>
                                <CardContent className="pt-4">
                                    <div className="text-[10.5px] uppercase tracking-widest text-stone-500">{label}</div>
                                    <div className="font-['Fraunces',serif] text-2xl mt-1">{val}</div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {tab === "contacts" && (
                    <Card data-testid="mkt-contacts-panel">
                        <CardHeader className="flex flex-row items-center gap-3">
                            <CardTitle className="text-base font-['Fraunces',serif]">Contacts</CardTitle>
                            <Input data-testid="mkt-contact-search" value={contactQ} onChange={e=>setContactQ(e.target.value)}
                                placeholder="Search email or name" className="max-w-xs" />
                            <Button data-testid="mkt-contact-search-btn" variant="outline" onClick={loadContacts}>Search</Button>
                            <div className="ml-auto">
                                <Button data-testid="mkt-add-contact-btn" onClick={()=>setShowContactDialog(true)}
                                    className="bg-[#0b1e3f] text-[#f6f3ec]">
                                    <Users size={14} className="mr-1" />Add contact
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {contacts.length === 0
                                ? <div className="text-sm text-stone-500 py-10 text-center">No contacts yet.</div>
                                : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm" data-testid="mkt-contacts-table">
                                        <thead>
                                            <tr className="text-left text-[10.5px] uppercase tracking-widest text-stone-500 border-b border-stone-200">
                                                <th className="py-2 pr-3">Email</th>
                                                <th className="py-2 pr-3">Name</th>
                                                <th className="py-2 pr-3">Type</th>
                                                <th className="py-2 pr-3">Stage</th>
                                                <th className="py-2 pr-3">Consent</th>
                                                <th className="py-2 pr-3">Source</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {contacts.map(c => (
                                                <tr key={c.id} className="border-b border-stone-100">
                                                    <td className="py-2 pr-3">{c.email}</td>
                                                    <td className="py-2 pr-3">{[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}</td>
                                                    <td className="py-2 pr-3">{c.contact_type}</td>
                                                    <td className="py-2 pr-3">{c.customer_stage}</td>
                                                    <td className="py-2 pr-3">
                                                        <StatusPill status={c.marketing_consent ? "PASS" : "BLOCKED"} />
                                                    </td>
                                                    <td className="py-2 pr-3">{c.source}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {tab === "import" && (
                    <Card data-testid="mkt-import-panel">
                        <CardHeader>
                            <CardTitle className="text-base font-['Fraunces',serif]">Import contacts (CSV)</CardTitle>
                            <div className="text-xs text-stone-500 mt-1">
                                Required column: <b>email</b>. Optional: first_name, last_name, source, country, consent_status (yes/no), consent_source, consent_date.
                                Rows with consent=yes but no consent_source will be rejected.
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <Textarea data-testid="mkt-import-textarea" rows={8} value={importText}
                                onChange={e=>setImportText(e.target.value)}
                                placeholder="email,first_name,consent_status,consent_source&#10;a@b.com,Alice,yes,web_form" />
                            <div className="flex gap-2">
                                <Button data-testid="mkt-import-preview-btn" variant="outline" onClick={previewImport} disabled={!importText || busy}>
                                    <Upload size={14} className="mr-1" />Preview
                                </Button>
                                <Button data-testid="mkt-import-commit-btn" onClick={commitImport}
                                    disabled={!importPreview || busy}
                                    className="bg-[#0b1e3f] text-[#f6f3ec]">
                                    Commit import
                                </Button>
                            </div>
                            {importPreview && (
                                <div className="mt-4 text-sm bg-stone-50 rounded-md p-3 border border-stone-200" data-testid="mkt-import-preview">
                                    <div className="font-medium mb-1">Preview</div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                        <div>Rows: <b>{importPreview.total_rows}</b></div>
                                        <div>Valid: <b>{importPreview.valid_count}</b></div>
                                        <div>Invalid: <b>{importPreview.invalid_count}</b></div>
                                        <div>Duplicates: <b>{importPreview.duplicates.length}</b></div>
                                        <div>Suppressed: <b>{importPreview.suppressed.length}</b></div>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {tab === "campaigns" && (
                    <Card data-testid="mkt-campaigns-panel">
                        <CardHeader className="flex flex-row items-center">
                            <CardTitle className="text-base font-['Fraunces',serif]">Campaigns</CardTitle>
                            <div className="ml-auto flex gap-2">
                                <Button data-testid="mkt-seed-launch-btn" variant="outline" onClick={seedFirstCampaign}>
                                    <Send size={14} className="mr-1" />Seed VAULT_LAUNCH_01
                                </Button>
                                <Button variant="outline" onClick={loadCampaigns}><RefreshCcw size={14} /></Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {campaigns.length === 0
                                ? <div className="text-sm text-stone-500 py-10 text-center">No campaigns yet.</div>
                                : (
                                <table className="w-full text-sm" data-testid="mkt-campaigns-table">
                                    <thead>
                                        <tr className="text-left text-[10.5px] uppercase tracking-widest text-stone-500 border-b border-stone-200">
                                            <th className="py-2 pr-3">Name</th>
                                            <th className="py-2 pr-3">Subject</th>
                                            <th className="py-2 pr-3">Segment</th>
                                            <th className="py-2 pr-3">Status</th>
                                            <th className="py-2 pr-3">UTM</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {campaigns.map(c => (
                                            <tr key={c.id} className="border-b border-stone-100">
                                                <td className="py-2 pr-3 font-medium">{c.name}</td>
                                                <td className="py-2 pr-3">{c.subject}</td>
                                                <td className="py-2 pr-3">{c.segment_key}</td>
                                                <td className="py-2 pr-3">
                                                    <StatusPill status={c.status === "DRAFT" ? "BLOCKED" : c.status === "SENT" ? "PASS" : "PASS_DRY_RUN"} />
                                                </td>
                                                <td className="py-2 pr-3">{c.utm_campaign}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            <div className="text-xs text-stone-500 mt-3 flex items-center gap-2">
                                <ShieldCheck size={12} /> Sends require: DRAFT → REVIEW → APPROVED → confirm=true. No autosend, ever.
                            </div>
                        </CardContent>
                    </Card>
                )}

                {tab === "readiness" && readiness && (
                    <Card data-testid="mkt-readiness-panel">
                        <CardHeader className="flex flex-row items-center">
                            <CardTitle className="text-base font-['Fraunces',serif]">Marketing Readiness Report</CardTitle>
                            <Button variant="outline" size="sm" className="ml-auto" onClick={loadReadiness}>
                                <RefreshCcw size={14} /> Refresh
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="mkt-readiness-grid">
                                {Object.entries(readiness.checks).map(([k, v]) => (
                                    <div key={k} className="flex items-start gap-3 p-3 rounded-lg border border-stone-200 bg-white">
                                        <div className="pt-0.5"><StatusPill status={v.status} /></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[11px] uppercase tracking-widest text-stone-500">
                                                {k.replace(/_/g, " ")}
                                            </div>
                                            <div className="text-sm text-stone-800 mt-0.5">{v.detail}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 text-xs text-stone-600 space-y-1">
                                {readiness.notes.map((n, i) => (<div key={i}>· {n}</div>))}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
                <DialogContent data-testid="mkt-add-contact-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Fraunces',serif]">Add marketing contact</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Input data-testid="mkt-contact-email" placeholder="Email"
                            value={contactForm.email}
                            onChange={e=>setContactForm({...contactForm, email: e.target.value})} />
                        <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="First name" value={contactForm.first_name}
                                onChange={e=>setContactForm({...contactForm, first_name: e.target.value})} />
                            <Input placeholder="Last name" value={contactForm.last_name}
                                onChange={e=>setContactForm({...contactForm, last_name: e.target.value})} />
                        </div>
                        <label className="flex items-center gap-2 text-sm mt-2">
                            <input data-testid="mkt-contact-consent" type="checkbox"
                                checked={contactForm.marketing_consent}
                                onChange={e=>setContactForm({...contactForm, marketing_consent: e.target.checked})} />
                            Send me useful VAULT tips, product updates and new features.
                        </label>
                        {contactForm.marketing_consent && (
                            <Input data-testid="mkt-contact-consent-source" placeholder="Consent source (required, e.g. web_form, live_event, ...)"
                                value={contactForm.consent_source}
                                onChange={e=>setContactForm({...contactForm, consent_source: e.target.value})} />
                        )}
                        {contactForm.marketing_consent && !consentBlockValid && (
                            <div className="text-xs text-rose-600">Consent source is required when the box is checked.</div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button data-testid="mkt-contact-save-btn" onClick={saveContact} disabled={busy || !contactForm.email || !consentBlockValid}
                            className="bg-[#0b1e3f] text-[#f6f3ec]">Save contact</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
